import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function VariblyPrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            <strong>
              Varibly is currently under development. Product images and additional details are coming soon.
            </strong>
          </p>
          <p>
            Varibly ("we," "our," or "us") is an AI-powered video ad generation platform operated by
            Castle Point Innovations, a division of Cuitt LLC. Varibly enables users to create
            personalized video advertisements using AI models, with project management, creative
            templates, and export capabilities.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and protect your information
            when you use Varibly. By accessing or using our platform, you agree to the practices
            described in this policy.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following types of information to provide and improve our services:</p>
          <ul>
            <li>
              <strong>Account Information:</strong> Email address and authentication credentials
              collected through Firebase Authentication when you create an account.
            </li>
            <li>
              <strong>Project and Campaign Data:</strong> Projects, campaigns, and associated metadata
              that you create and manage within the platform.
            </li>
            <li>
              <strong>Creative Content:</strong> Templates, creative variations, and customization
              settings you configure for your video advertisements.
            </li>
            <li>
              <strong>Video Rendering Data:</strong> Rendering jobs, processing status, and generated
              video content produced through our AI-powered pipeline.
            </li>
            <li>
              <strong>Uploaded Files:</strong> Images, media assets, and other files you upload to
              the platform, as well as records of file downloads and exports.
            </li>
            <li>
              <strong>User Preferences and Settings:</strong> Application preferences, workspace
              configurations, and display settings you customize.
            </li>
            <li>
              <strong>Payment Information:</strong> Billing details and payment data processed
              securely through Stripe. We do not store full credit card numbers on our servers.
            </li>
            <li>
              <strong>Usage Analytics:</strong> Information about how you interact with the platform,
              including feature usage, session data, and performance metrics.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, operate, and maintain the Varibly platform and its features.</li>
            <li>Process and render AI-generated video advertisements based on your inputs.</li>
            <li>Manage your account, projects, campaigns, and creative assets.</li>
            <li>Process payments and manage your subscription or billing.</li>
            <li>Improve our AI models, platform performance, and user experience.</li>
            <li>Communicate with you about your account, updates, and service-related notices.</li>
            <li>Monitor platform usage to ensure security, prevent abuse, and maintain system reliability.</li>
            <li>Comply with legal obligations and enforce our terms of service.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>
            Varibly integrates with the following third-party services to deliver its functionality.
            Each service operates under its own privacy policy:
          </p>
          <ul>
            <li>
              <strong>Firebase Authentication:</strong> Manages user sign-up, sign-in, and account
              security.
            </li>
            <li>
              <strong>Cloud Firestore:</strong> Stores project data, campaign configurations,
              templates, and user preferences.
            </li>
            <li>
              <strong>Google Cloud Storage:</strong> Hosts uploaded files, media assets, and
              generated video content.
            </li>
            <li>
              <strong>Google Cloud Tasks:</strong> Manages asynchronous video rendering jobs and
              background processing workflows.
            </li>
            <li>
              <strong>Google Cloud Run:</strong> Provides the scalable infrastructure that powers our
              backend services and rendering pipeline.
            </li>
            <li>
              <strong>Google Generative AI (Gemini API):</strong> Powers AI-driven creative
              generation and content optimization features.
            </li>
            <li>
              <strong>Anthropic Claude API:</strong> Provides additional AI capabilities for content
              generation and creative assistance.
            </li>
            <li>
              <strong>Stripe:</strong> Processes payments and manages billing securely. Stripe may
              collect and process payment data in accordance with its own privacy policy.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            Your data is stored using Google Cloud infrastructure, including Cloud Firestore and
            Google Cloud Storage. We implement industry-standard security measures to protect your
            information, including encryption in transit and at rest, secure authentication, and
            access controls.
          </p>
          <p>
            Payment data is handled exclusively by Stripe and is subject to Stripe's PCI-compliant
            security standards. We do not store sensitive payment credentials on our servers.
          </p>
          <p>
            While we take reasonable precautions to safeguard your data, no method of electronic
            storage or transmission is completely secure. We cannot guarantee absolute security of
            your information.
          </p>
        </>
      ),
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>
            We retain your account information, project data, and generated content for as long as
            your account is active or as needed to provide our services. If you delete your account,
            we will remove your personal data within a reasonable timeframe, except where retention
            is required by law or for legitimate business purposes such as resolving disputes or
            enforcing our agreements.
          </p>
          <p>
            Rendered video content and uploaded files may be retained for a limited period after
            account deletion to allow for data recovery requests, after which they will be
            permanently deleted.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate or incomplete data.</li>
            <li>Request deletion of your personal data and account.</li>
            <li>Export your project data and generated content.</li>
            <li>Withdraw consent for data processing where applicable.</li>
            <li>Object to or restrict certain processing of your information.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:contact@castlepointinnovations.com">contact@castlepointinnovations.com</a>.
            We will respond to your request within a reasonable timeframe in accordance with
            applicable law.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            Varibly is not intended for use by individuals under the age of 13. We do not knowingly
            collect personal information from children under 13. If we become aware that we have
            collected data from a child under 13, we will take steps to delete that information
            promptly. If you believe a child has provided us with personal data, please contact us
            at{' '}
            <a href="mailto:contact@castlepointinnovations.com">contact@castlepointinnovations.com</a>.
          </p>
        </>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            services, or legal requirements. When we make material changes, we will update the "Last
            Updated" date at the top of this page and, where appropriate, notify you via email or
            through the platform. We encourage you to review this policy periodically.
          </p>
        </>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our
            data practices, please contact us at:
          </p>
          <p>
            <strong>Castle Point Innovations — Cuitt LLC</strong>
            <br />
            Email:{' '}
            <a href="mailto:contact@castlepointinnovations.com">contact@castlepointinnovations.com</a>
          </p>
        </>
      ),
    },
  ];

  return <PrivacyPolicy productName="Varibly" lastUpdated="March 27, 2026" sections={sections} />;
}

export default VariblyPrivacy;
